import { Button, Checkbox, Tooltip } from 'antd';
import LogOut from 'assets/svg/LogOut';
import { useEffect, useState } from 'react';

import { formatPpaViewUpdatedAt } from 'utils/ppaHelpers';

import { getViewNameValidationError } from '../savedViewStateSelect.helpers';

import {
    CheckIcon,
    CrossIcon,
    DeleteButtonIcon,
    EditIconButton,
    LoadViewButtonIcon,
    SystemViewHomeIcon,
    UpdateViewButtonIcon,
} from '../SavedViewStateSelect.icons';
import { SAVE_VIEW_TOOLTIP_PROPS, UPDATED_VIEW_MODIFIED_TOOLTIP } from '../savedViewStateSelect.constants';
import { DELETE_CONFIRM_ICON_BTN_CLASS } from '../savedViewStateSelect.styles';
import type { SavedViewListItemProps } from '../SavedViewStateSelect.types';
import DefaultViewTag from './DefaultViewTag';
import SavedViewRenameEditor from './SavedViewRenameEditor';

const SavedViewListItem = ({
    savedView,
    existingViewNames,
    isExpanded,
    loadingAction,
    isActiveView,
    isViewModified,
    showModifiedIndicator,
    onSelect,
    onLoad,
    onDelete,
    onSetDefault,
    onRename,
    onUpdateView,
    onExitView,
}: SavedViewListItemProps) => {
    const isCustomView = savedView.view_type === 'CUSTOM';
    const isSystemView = savedView.view_type === 'SYSTEM';
    const savedOnLabel = formatPpaViewUpdatedAt(savedView.updated_at ?? savedView.created_at ?? undefined);
    const [isRenaming, setIsRenaming] = useState(false);
    const [isUpdateConfirming, setIsUpdateConfirming] = useState(false);
    const [isDeleteConfirming, setIsDeleteConfirming] = useState(false);
    const [draftName, setDraftName] = useState(savedView.view_name);
    const rowClassName = isRenaming
        ? 'py-2'
        : isActiveView
          ? 'border-l-[3px] border-[#5f49bc] bg-[#f7f5ff] py-2'
          : isExpanded
            ? 'bg-[#f4f4f5] py-2'
            : 'py-[6px]';

    useEffect(() => {
        setDraftName(savedView.view_name);
    }, [savedView.view_name]);

    useEffect(() => {
        if (!isExpanded) {
            setIsRenaming(false);
            setIsUpdateConfirming(false);
            setIsDeleteConfirming(false);
        }
    }, [isExpanded]);

    const handleStartRename = (event: { stopPropagation: () => void }) => {
        event.stopPropagation();
        setDraftName(savedView.view_name);
        setIsRenaming(true);
    };

    const handleCancelRename = () => {
        setDraftName(savedView.view_name);
        setIsRenaming(false);
    };

    const handleConfirmRename = async () => {
        const trimmedName = draftName.trim();
        const validationError = getViewNameValidationError(
            draftName,
            existingViewNames,
            savedView.view_name,
        );
        if (!trimmedName || trimmedName === savedView.view_name.trim() || validationError) {
            return;
        }
        const didRename = await onRename(savedView, trimmedName);
        if (didRename) {
            setIsRenaming(false);
        }
    };

    const handleStartDeleteConfirmation = (event: { stopPropagation: () => void }) => {
        event.stopPropagation();
        setIsRenaming(false);
        setIsUpdateConfirming(false);
        setIsDeleteConfirming(true);
    };

    const handleCancelDelete = () => {
        setIsDeleteConfirming(false);
    };

    const handleConfirmDelete = async () => {
        try {
            await onDelete(savedView);
            setIsDeleteConfirming(false);
        } catch {
            // Keep inline confirmation open when delete fails.
        }
    };

    const handleStartUpdateConfirmation = (event: { stopPropagation: () => void }) => {
        event.stopPropagation();
        if (!isViewModified) {
            return;
        }
        setIsRenaming(false);
        setIsUpdateConfirming(true);
    };

    const handleCancelUpdate = () => {
        setIsUpdateConfirming(false);
    };

    const handleConfirmUpdate = async () => {
        await onUpdateView(savedView);
        setIsUpdateConfirming(false);
    };

    const viewRowButton = (
        <button type="button" onClick={() => onSelect(savedView.view_id)} className="w-full text-left">
            <div className="flex flex-col gap-[6px]">
                {savedView.is_default ? <DefaultViewTag /> : null}
                <div className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 flex-1 items-center gap-1.5">
                        {isSystemView ? <SystemViewHomeIcon /> : null}
                        <div className="min-w-0 flex-1 truncate text-[14px] leading-[22px] text-[#18181b]">
                            {savedView.view_name}
                            {showModifiedIndicator ? '*' : ''}
                        </div>
                    </div>
                    {isCustomView && isExpanded && !isUpdateConfirming && !isDeleteConfirming ? (
                        <button
                            type="button"
                            onClick={handleStartRename}
                            className="mt-0.5 flex shrink-0 items-center justify-center rounded p-0.5 hover:bg-black/[0.04]"
                            aria-label={`Rename ${savedView.view_name}`}
                        >
                            <EditIconButton />
                        </button>
                    ) : null}
                </div>
                {savedOnLabel && isCustomView ? (
                    <div className="text-[10px] leading-[14px] text-[#71717b]">{savedOnLabel}</div>
                ) : null}
            </div>
        </button>
    );

    const isExitLoading = loadingAction === 'exit';
    const isLoadLoading = loadingAction === 'load';
    const isUpdateLoading = loadingAction === 'update';
    const isDeleteLoading = loadingAction === 'delete';
    const isRenameLoading = loadingAction === 'rename';
    const isAnyActionLoading = loadingAction != null;
    const isExitBlocked = isAnyActionLoading && !isExitLoading;
    const isLoadBlocked = isAnyActionLoading && !isLoadLoading;
    const isUpdateBlocked = isAnyActionLoading && !isUpdateLoading;

    return (
        <div className={`px-3 ${rowClassName}`}>
            {isRenaming ? (
                <SavedViewRenameEditor
                    value={draftName}
                    originalViewName={savedView.view_name}
                    existingViewNames={existingViewNames}
                    isSaving={isRenameLoading}
                    onChange={setDraftName}
                    onCancel={handleCancelRename}
                    onConfirm={handleConfirmRename}
                />
            ) : showModifiedIndicator ? (
                <Tooltip {...SAVE_VIEW_TOOLTIP_PROPS} title={UPDATED_VIEW_MODIFIED_TOOLTIP}>
                    <span className="block w-full">{viewRowButton}</span>
                </Tooltip>
            ) : (
                viewRowButton
            )}
            {!isRenaming && isExpanded ? (
                <div className="mt-3 flex flex-col gap-2">
                    {isDeleteConfirming && isCustomView ? (
                        <div className="flex h-[28px] w-full items-center justify-between gap-[6px]">
                            <div className="flex-1 text-[12px] font-medium text-[#ff4d4f]">Delete this View?</div>
                            <div className="flex items-center gap-[6px]">
                                <div
                                    className={`${DELETE_CONFIRM_ICON_BTN_CLASS} !border-[#d9d9d9] !bg-[#ffffff]`}
                                    onClick={handleCancelDelete}
                                    aria-label={`Cancel delete ${savedView.view_name}`}
                                >
                                    <CrossIcon />
                                </div>
                                <div
                                    className={`${DELETE_CONFIRM_ICON_BTN_CLASS} !border-[#44338a] !bg-[#44338A] ${isDeleteLoading ? 'pointer-events-none opacity-60' : ''}`}
                                    onClick={() => {
                                        if (!isDeleteLoading) {
                                            void handleConfirmDelete();
                                        }
                                    }}
                                    aria-label={`Confirm delete ${savedView.view_name}`}
                                >
                                    <CheckIcon />
                                </div>
                            </div>
                        </div>
                    ) : isUpdateConfirming && isCustomView && isActiveView ? (
                        <div className="flex h-[28px] w-full items-center justify-between gap-[6px]">
                            <div className="flex-1 text-[12px] font-medium text-[#44338a]">
                                Update and save this view?
                            </div>
                            <div className="flex items-center gap-[6px]">
                                <div
                                    className={`${DELETE_CONFIRM_ICON_BTN_CLASS} !border-[#d9d9d9] !bg-[#ffffff]`}
                                    onClick={handleCancelUpdate}
                                    aria-label={`Cancel update ${savedView.view_name}`}
                                >
                                    <CrossIcon />
                                </div>
                                <div
                                    className={`${DELETE_CONFIRM_ICON_BTN_CLASS} !border-[#44338a] !bg-[#44338A] ${isUpdateLoading ? 'pointer-events-none opacity-60' : ''}`}
                                    onClick={() => {
                                        if (!isUpdateLoading) {
                                            void handleConfirmUpdate();
                                        }
                                    }}
                                    aria-label={`Confirm update ${savedView.view_name}`}
                                >
                                    <CheckIcon />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2">
                            {isActiveView ? (
                                <Button
                                    type="primary"
                                    size="small"
                                    loading={isExitLoading}
                                    className={`!flex !h-[24px] !items-center !justify-center !gap-2 !rounded !border-[#44338a] !bg-[#44338a] !px-2 !text-[12px] !font-medium ${isExitBlocked ? 'pointer-events-none' : ''}`}
                                    icon={<LogOut />}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        if (isExitBlocked) {
                                            return;
                                        }
                                        onExitView(savedView);
                                    }}
                                >
                                    Exit View
                                </Button>
                            ) : (
                                <Button
                                    type="primary"
                                    size="small"
                                    loading={isLoadLoading}
                                    className={`!flex !h-[24px] !items-center !justify-center !gap-2 !rounded !border-[#44338a] !bg-[#44338a] !px-2 !text-[12px] !font-medium ${isLoadBlocked ? 'pointer-events-none' : ''}`}
                                    icon={<LoadViewButtonIcon />}
                                    onClick={() => {
                                        if (isLoadBlocked) {
                                            return;
                                        }
                                        onLoad(savedView);
                                    }}
                                >
                                    Load View
                                </Button>
                            )}
                            {isCustomView && isActiveView ? (
                                <button
                                    type="button"
                                    disabled={!isViewModified || isUpdateBlocked || isUpdateLoading}
                                    className={`flex shrink-0 items-center justify-center rounded p-0.5 text-[#44338a] disabled:cursor-not-allowed disabled:opacity-40 enabled:cursor-pointer enabled:hover:bg-black/[0.04] ${isUpdateBlocked || isUpdateLoading ? 'pointer-events-none opacity-60' : ''}`}
                                    aria-label={`Update ${savedView.view_name}`}
                                    onClick={(event) => {
                                        if (!isViewModified || isUpdateBlocked || isUpdateLoading) {
                                            return;
                                        }
                                        handleStartUpdateConfirmation(event);
                                    }}
                                >
                                    <UpdateViewButtonIcon />
                                </button>
                            ) : null}
                            {isCustomView ? (
                                <button
                                    type="button"
                                    className={`flex shrink-0 items-center justify-center rounded p-0.5 text-[#44338a] hover:bg-black/[0.04] ${isAnyActionLoading ? 'pointer-events-none opacity-60' : 'cursor-pointer'}`}
                                    aria-label={`Delete ${savedView.view_name}`}
                                    onClick={handleStartDeleteConfirmation}
                                >
                                    <DeleteButtonIcon />
                                </button>
                            ) : null}
                        </div>
                    )}
                    {!isUpdateConfirming && !isDeleteConfirming && !savedView.is_default && isActiveView ? (
                        <Checkbox
                            checked={false}
                            className={`!text-[12px] !text-fontColor ${isAnyActionLoading ? 'pointer-events-none' : ''}`}
                            onChange={(event) => {
                                if (isAnyActionLoading) {
                                    return;
                                }
                                onSetDefault(savedView, event.target.checked);
                            }}
                        >
                            Set as Default
                        </Checkbox>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

export default SavedViewListItem;
